import biLogger from '@wix/web-bi-logger';

import {
  BiEvent,
  BiEventIDs,
  BiLogger,
  BiEventParams,
  PlanTypeInfoBiEvent,
  AppLoadBiEvent,
  ChangeStepBiEvent,
  CheckoutPageBiEvent,
  DoublePurchaseBiEvent,
  BiEventDefaultParams,
  EventActionMap,
  SelectPlanBiEvent,
  SelectCycleBiEvent,
  RequestCallCTABiEvent,
  ViewProductFeatureTooltipBiEvent,
  CheckoutPageLoadedBiEvent,
  ChangePlanBiEvent,
  ViewCurrentPlanBiEvent,
  ViewCycleDiscountTooltip,
  ClickOnFAQItem,
  ClickOnShowFeatures,
  ClickOnShowAllFeatures,
  RequestCallPopupLoadBiEvent,
  CheckoutPageViewTooltip,
  SelectPlanRadioButtonsMobileGrid,
  StickyHeaderMobileGrid,
} from './BiLoggerTypes';
import { Brands } from '../../enums';

const brandHostMap: { [key in Brands]: string } = {
  [Brands.WIX]: 'www.wix.com',
  [Brands.EDITOR_X]: 'manage.editorx.com',
};

export const BiLoggerFactory = (
  defaults: BiEventDefaultParams,
  brand: Brands,
  _biLogger_ = biLogger,
): BiLogger => {
  const logger: { log(event: Partial<BiEvent>): void } = _biLogger_
    .factory({
      host: `${brandHostMap[brand]}/store/bi`,
      endpoint: 'plansPurchase',
      useBeacon: true,
    })
    .updateDefaults({
      referralAdditionalInfo: '',
      referer: '',
      ...defaults,
      abTesting: 'purchase_wix_plan_static',
    })
    .logger();

  function getBiEventData<T>(
    additionalParams: BiEventParams<T>,
    evid: BiEventIDs,
  ): Partial<BiEvent> {
    return { evid, ...additionalParams, action: EventActionMap[evid || 0] };
  }

  const logBiEvent = (event: Partial<BiEvent>): void => {
    Object.keys(event).forEach((key) => {
      const value = event[key];
      event[key] = typeof value === 'object' ? JSON.stringify(value) : value;
    });
    logger.log(event);
  };

  const logSelectPlanBiEvent = (
    additionalParams: BiEventParams<SelectPlanBiEvent>,
  ) => logBiEvent(getBiEventData(additionalParams, BiEventIDs.A_PLAN_SELECTED));

  const logChangeTabBiEvent = (
    additionalParams: BiEventParams<PlanTypeInfoBiEvent>,
  ) => logBiEvent(getBiEventData(additionalParams, BiEventIDs.CHANGE_PP_TYPE));

  const logPlansPageLoadedBiEvent = (
    additionalParams: BiEventParams<AppLoadBiEvent>,
  ) =>
    logBiEvent(
      getBiEventData(
        { ...additionalParams, viewMode: 'siteLevel' },
        BiEventIDs.PACKAGE_PICKER_LOADED,
      ),
    );

  const logSelectCycleBiEvent = (
    additionalParams: BiEventParams<SelectCycleBiEvent>,
  ) =>
    logBiEvent(getBiEventData(additionalParams, BiEventIDs.A_CYCLE_SELECTED));

  const logPaymentPageReadyBiEvent = (
    additionalParams: BiEventParams<CheckoutPageLoadedBiEvent>,
  ) =>
    logBiEvent(
      getBiEventData(additionalParams, BiEventIDs.A_PAYMENT_PAGE_READY),
    );

  const logStepBackBiEvent = (
    additionalParams: BiEventParams<ChangeStepBiEvent>,
  ) =>
    logBiEvent(
      getBiEventData(
        { ...additionalParams, abTesting: undefined },
        BiEventIDs.PREVIOUS_STEP_SELECT,
      ),
    );

  const logSubmitPurchaseClickedBiEvent = (
    additionalParams: BiEventParams<CheckoutPageBiEvent>,
  ) =>
    logBiEvent(
      getBiEventData(additionalParams, BiEventIDs.ORDER_PAGE_SUBMIT_PRESSED),
    );

  const logPurchaseSuccessBiEvent = (
    additionalParams: BiEventParams<CheckoutPageBiEvent>,
  ) =>
    logBiEvent(
      getBiEventData(
        additionalParams,
        BiEventIDs.R_PREMIUM_ORDER_PAGE_SUMBIT_SUCCESS,
      ),
    );

  const logPurchasePendingBiEvent = (
    additionalParams: BiEventParams<CheckoutPageBiEvent>,
  ) =>
    logBiEvent(
      getBiEventData(
        additionalParams,
        BiEventIDs.R_PREMIUM_ORDER_PAGE_SUBMIT_PENDING,
      ),
    );

  const logOrderPageBusinessErrorBiEvent = (
    additionalParams: BiEventParams<CheckoutPageBiEvent>,
  ) =>
    logBiEvent(
      getBiEventData(additionalParams, BiEventIDs.ORDER_PAGE_BUSINESS_ERROR),
    );

  const logOrderPageFatalErrorBiEvent = (
    additionalParams: BiEventParams<CheckoutPageBiEvent>,
  ) =>
    logBiEvent(
      getBiEventData(additionalParams, BiEventIDs.ORDER_PAGE_FATAL_ERROR),
    );

  const logOrderPageValidationErrorBiEvent = (
    additionalParams: BiEventParams<CheckoutPageBiEvent>,
  ) =>
    logBiEvent(
      getBiEventData(additionalParams, BiEventIDs.ORDER_PAGE_VALIDATION_ERROR),
    );

  const logDoublePurchasePopupCTA = () =>
    logBiEvent({
      evid: BiEventIDs.A_DOUBLE_PURCHASE_POPUP_VIEW_BUTTON_CLICKED,
    });

  const logTabsTooltipClosed = () =>
    logBiEvent({ evid: BiEventIDs.TABS_TOOLTIP_CLOSED });

  const logDoublePurchasePopupViewed = (
    additionalParams: BiEventParams<DoublePurchaseBiEvent>,
  ) =>
    logBiEvent(
      getBiEventData(additionalParams, BiEventIDs.A_VIEW_DOUBLE_PURCHASE_POPUP),
    );

  const logRequestCallPopupLoad = (
    additionalParams: BiEventParams<RequestCallPopupLoadBiEvent>,
  ) =>
    logBiEvent({
      evid: BiEventIDs.REQUEST_CALL_POPUP_LOAD,
      ...additionalParams,
    });

  const logRequestCallPopupCTA = (
    additionalParams: BiEventParams<RequestCallCTABiEvent>,
  ) =>
    logBiEvent({
      evid: BiEventIDs.REQUEST_CALL_POPUP_CTA,
      ...additionalParams,
    });

  const logViewProductFeatureTooltip = (
    additionalParams: BiEventParams<ViewProductFeatureTooltipBiEvent>,
  ) =>
    logBiEvent({
      evid: BiEventIDs.VIEW_PRODUCT_FEATURE_TOOLTIP,
      ...additionalParams,
    });

  const logCurrentPlanModalOpened = (
    additionalParams: BiEventParams<ViewCurrentPlanBiEvent>,
  ) =>
    logBiEvent(
      getBiEventData(
        additionalParams,
        BiEventIDs.VIEW_CURRENT_PLAN_MODAL_OPENED,
      ),
    );

  const logChangePlanPopupOpened = (
    additionalParams: BiEventParams<ChangePlanBiEvent>,
  ) =>
    logBiEvent(
      getBiEventData(additionalParams, BiEventIDs.CHANGE_PLAN_POPUP_OPENED),
    );

  const logChangePlanPopupClickChange = (
    additionalParams: BiEventParams<ChangePlanBiEvent>,
  ) =>
    logBiEvent(
      getBiEventData(
        additionalParams,
        BiEventIDs.CHANGE_PLAN_POPUP_CLICK_CHANGE,
      ),
    );

  const logChangePlanPopupClosed = (
    additionalParams: BiEventParams<ChangePlanBiEvent>,
  ) =>
    logBiEvent(
      getBiEventData(additionalParams, BiEventIDs.CHANGE_PLAN_POPUP_CLOSED),
    );

  const logDiscountTooltipIsShown = (
    additionalParams: BiEventParams<ViewCycleDiscountTooltip>,
  ) =>
    logBiEvent(
      getBiEventData(additionalParams, BiEventIDs.VIEW_CYCLE_DISCOUNT_TOOLTIP),
    );

  const logClickOnFAQItem = (additionalParams: BiEventParams<ClickOnFAQItem>) =>
    logBiEvent(getBiEventData(additionalParams, BiEventIDs.CLICK_ON_FAQ_ITEM));
  const logClickOnShowFeatures = (
    additionalParams: BiEventParams<ClickOnShowFeatures>,
  ) =>
    logBiEvent(
      getBiEventData(additionalParams, BiEventIDs.CLICK_ON_SHOW_FEATURES),
    );
  const logClickOnShowAllFeatures = (
    additionalParams: BiEventParams<ClickOnShowAllFeatures>,
  ) =>
    logBiEvent(
      getBiEventData(additionalParams, BiEventIDs.CLICK_ON_SHOW_ALL_FEATURES),
    );

  const logCheckoutPageViewTooltipBiEvent = (
    additionalParams: BiEventParams<CheckoutPageViewTooltip>,
  ) =>
    logBiEvent(
      getBiEventData(additionalParams, BiEventIDs.CHECKOUT_PAGE_VIEW_TOOLTIP),
    );

  const logSelectPlanRadioButtonsMobileGrid = (
    additionalParams: BiEventParams<SelectPlanRadioButtonsMobileGrid>,
  ) =>
    logBiEvent(
      getBiEventData(
        additionalParams,
        BiEventIDs.SELECT_PLAN_RADIO_BUTTONS_MOBILE_GRID,
      ),
    );

  const logStickyHeaderMobileGrid = (
    additionalParams: BiEventParams<StickyHeaderMobileGrid>,
  ) =>
    logBiEvent(
      getBiEventData(additionalParams, BiEventIDs.STICKY_HEADER_MOBILE_GRID),
    );

  return {
    logChangeTabBiEvent,
    logPlansPageLoadedBiEvent,
    logSelectPlanBiEvent,
    logSelectCycleBiEvent,
    logPaymentPageReadyBiEvent,
    logStepBackBiEvent,
    logSubmitPurchaseClickedBiEvent,
    logPurchaseSuccessBiEvent,
    logPurchasePendingBiEvent,
    logOrderPageBusinessErrorBiEvent,
    logOrderPageFatalErrorBiEvent,
    logOrderPageValidationErrorBiEvent,
    logDoublePurchasePopupCTA,
    logDoublePurchasePopupViewed,
    logRequestCallPopupLoad,
    logRequestCallPopupCTA,
    logViewProductFeatureTooltip,
    logTabsTooltipClosed,
    logCurrentPlanModalOpened,
    logChangePlanPopupOpened,
    logChangePlanPopupClickChange,
    logChangePlanPopupClosed,
    logDiscountTooltipIsShown,
    logClickOnFAQItem,
    logClickOnShowFeatures,
    logClickOnShowAllFeatures,
    logCheckoutPageViewTooltipBiEvent,
    logSelectPlanRadioButtonsMobileGrid,
    logStickyHeaderMobileGrid,
  };
};
